import { useFragment, type FragmentType } from '@/shared/graphql/router'
import {
  FileType,
  ScoreLevel,
  type CheckerDetectionResultFragment,
} from '@/shared/graphql/router/graphql'

import { CHECKER_DETECTION_RESULT_FRAGMENT } from './operations'

export type DetectionResult = CheckerDetectionResultFragment
export type Finding = DetectionResult['findings'][number]

export const readDetection = (
  result:
    | FragmentType<typeof CHECKER_DETECTION_RESULT_FRAGMENT>
    | null
    | undefined
): DetectionResult | null =>
  result ? useFragment(CHECKER_DETECTION_RESULT_FRAGMENT, result) : null

export const findingKey = (finding: Finding) =>
  finding.fileDataId
    ? `file:${finding.fileDataId}`
    : finding.registrationId
      ? `registration:${finding.registrationId}`
      : `${finding.algorithm}:${finding.url ?? ''}:${finding.title ?? ''}`

export const findingLevel = (finding: Finding): ScoreLevel => finding.riskLevel

const LEVEL_RANK: Record<ScoreLevel, number> = {
  [ScoreLevel.High]: 0,
  [ScoreLevel.Medium]: 1,
  [ScoreLevel.Low]: 2,
}

const compareFindings = (a: Finding, b: Finding) =>
  LEVEL_RANK[findingLevel(a)] - LEVEL_RANK[findingLevel(b)] ||
  Number(b.scoreKind === 'algorithm_similarity_percent') -
    Number(a.scoreKind === 'algorithm_similarity_percent') ||
  Number(b.audioCompositionEvidence?.exactLaneMatch === true) -
    Number(a.audioCompositionEvidence?.exactLaneMatch === true) ||
  Number(b.strict === true) - Number(a.strict === true) ||
  (a.scoreKind === b.scoreKind
    ? (b.scorePercent ?? b.score ?? -Infinity) -
      (a.scorePercent ?? a.score ?? -Infinity)
    : 0) ||
  0

export function mergeFindings(
  retained: ReadonlyMap<string, Finding>,
  incoming: readonly Finding[]
): Map<string, Finding> {
  const next = new Map(retained)
  for (const finding of incoming) {
    const key = findingKey(finding)
    const previous = next.get(key)
    if (!previous || compareFindings(finding, previous) <= 0)
      next.set(key, finding)
  }
  return next
}

export const sortFindings = (findings: Iterable<Finding>) =>
  [...findings].sort(compareFindings)

export function findingFileType(finding: Finding): FileType {
  switch (finding.modality) {
    case 'audio':
      return FileType.Audio
    case 'video':
      return FileType.Video
    case 'image':
      return FileType.Image
    case 'document':
      return FileType.Pdf
    case 'text':
      return FileType.Text
    default:
      return FileType.Generic
  }
}

export const findingTitle = (finding: Finding) =>
  finding.title ?? 'Registered work'
